import { useState, useRef, useEffect } from "react";
import Image from "next/image";
import styled from "styled-components";
import { getColor } from "@/styles/ThemeConfig";
import { breakpoints } from "@/styles/sizes";

const Section = styled.div`
    padding: 60px 0 80px;
    background: #fff;
`;

const Headline = styled.h2`
    font-family: 'Josefin Sans', sans-serif;
    font-size: 36px;
    font-weight: 700;
    color: ${getColor('green')};
    text-transform: uppercase;
    text-align: center;
    margin: 0 0 40px;

    @media only screen and (${breakpoints.mobile}) {
        font-size: 25px;
        margin-bottom: 24px;
        text-align: left;
    }
`;

/* ---- 1-post layout ---- */
const SinglePost = styled.a`
    display: flex;
    gap: 50px;
    align-items: flex-start;
    text-decoration: none;
    color: inherit;

    @media only screen and (${breakpoints.mobile}) {
        flex-direction: column;
        gap: 0;
    }
`;

const SingleImageWrap = styled.div`
    flex: 0 0 42%;
    position: relative;
    aspect-ratio: 3 / 2;

    @media only screen and (${breakpoints.mobile}) {
        flex: none;
        width: 100%;
    }
`;

const SingleContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 10px 0;
`;

/* ---- multi-post grid ---- */
const GridArea = styled.div`
    position: relative;
`;

const GridOuter = styled.div`
    overflow: hidden;

    @media only screen and (${breakpoints.mobile}) {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        &::-webkit-scrollbar { display: none; }
    }
`;

const Track = styled.div`
    display: flex;
    gap: 30px;
    transition: transform 0.4s ease;

    @media only screen and (${breakpoints.mobile}) {
        gap: 16px;
        padding-bottom: 8px;
    }
`;

const SideArrowLeft = styled.button`
    position: absolute;
    top: 40%;
    transform: translateY(-50%);
    left: -64px;
    width: 48px;
    height: 48px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${getColor('gold')};
    transition: color 0.2s, opacity 0.2s;

    &:disabled { opacity: 0.25; cursor: default; }
    &:hover:not(:disabled) { color: ${getColor('darkGreen')}; }

    @media only screen and (${breakpoints.mobile}) {
        display: none;
    }
`;

const SideArrowRight = styled(SideArrowLeft)`
    left: auto;
    right: -64px;
`;

const Card = styled.a`
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
    flex-shrink: 0;

    @media only screen and (${breakpoints.mobile}) {
        flex: 0 0 270px;
    }
`;

const CardImageWrap = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 2;
    margin-bottom: 16px;

    @media only screen and (${breakpoints.mobile}) {
        aspect-ratio: 1 / 1;
    }
`;

/* ---- shared text ---- */
const PostTitle = styled.div`
    font-family: 'Playfair Display', serif;
    font-size: 24px;
    font-weight: 500;
    color: #000;
    line-height: 100%;
    margin-bottom: 16px;

    @media only screen and (${breakpoints.mobile}) {
        font-size: 20px;
    }
`;

const Excerpt = styled.div`
    font-family: 'Josefin Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #000;
    line-height: 20px;
    flex: 1;
`;

const ReadMore = styled.span`
    display: inline-block;
    margin-top: 16px;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: ${getColor('gold')};
    transition: all 0.3s ease;

    &:hover { color: ${getColor('darkGreen')}; }
`;

/* ---- read all button ---- */
const ReadAllWrap = styled.div`
    margin-top: 48px;
    display: flex;
    justify-content: center;

    @media only screen and (${breakpoints.mobile}) {
        justify-content: flex-start;
    }
`;

const ReadAllBtn = styled.a`
    display: inline-block;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: ${getColor('darkGreen')};
    padding: 14px 20px 10px 20px;
    text-decoration: none;
    border: 1px solid ${getColor('darkGreen')};
    transition: all 0.3s ease;

    &:hover { color: ${getColor('green')}; border-color: ${getColor('green')}; }
`;

const ArrowIcon = ({ flip }) => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"
        style={{ transform: flip ? 'scaleX(-1)' : 'none' }}>
        <circle cx="24" cy="24" r="23" stroke="currentColor" strokeWidth="1.5" />
        <path d="M21 16l8 8-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export default function CityBlogSection({
    cityName,
    posts = [],
    readAllLink,
    readAllText
}) {
    const [startIndex, setStartIndex] = useState(0);
    const [cardWidth, setCardWidth] = useState(null);
    const outerRef = useRef(null);

    const columns = Math.min(posts.length, 3) || 1;
    const gap = 30;
    const canPrev = startIndex > 0;
    const canNext = startIndex + columns < posts.length;
    const translateX = cardWidth !== null ? -startIndex * (cardWidth + gap) : 0;

    useEffect(() => {
        const measure = () => {
            if (!outerRef.current) return;
            setCardWidth((outerRef.current.offsetWidth - (columns - 1) * gap) / columns);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [columns]);

    if (!posts.length) return null;

    return (
        <Section>
            <div className="container_slider" style={{ padding: '0 20px' }}>
                <Headline>Read the latest on {cityName} from our blog</Headline>

                {columns === 1 ? (
                    <SinglePost href={posts[0].link}>
                        <SingleImageWrap>
                            <Image src={posts[0].imageUrl} alt={posts[0].title} fill style={{ objectFit: 'cover' }} />
                        </SingleImageWrap>
                        <SingleContent>
                            <PostTitle>{posts[0].title}</PostTitle>
                            <Excerpt>{posts[0].excerpt}</Excerpt>
                            <ReadMore>Read More →</ReadMore>
                        </SingleContent>
                    </SinglePost>
                ) : (
                    <GridArea>
                        {posts?.length > 3 && (
                        <SideArrowLeft disabled={!canPrev} onClick={() => setStartIndex(i => i - 1)} aria-label="Previous">
                            <ArrowIcon flip />
                        </SideArrowLeft>)}

                        <GridOuter ref={outerRef}>
                            <Track style={{ transform: `translateX(${translateX}px)` }}>
                                {posts.map((post, i) => (
                                    <Card key={i} href={post.link} style={{ width: cardWidth ? `${cardWidth}px` : undefined }}>
                                        <CardImageWrap>
                                            <Image src={post.imageUrl} alt={post.title} fill loading="eager" style={{ objectFit: 'cover' }} />
                                        </CardImageWrap>
                                        <PostTitle>{post.title}</PostTitle>
                                        <Excerpt>{post.excerpt}</Excerpt>
                                        <ReadMore>Read More →</ReadMore>
                                    </Card>
                                ))}
                            </Track>
                        </GridOuter>
                        {posts?.length > 3 && (
                        <SideArrowRight disabled={!canNext} onClick={() => setStartIndex(i => i + 1)} aria-label="Next">
                            <ArrowIcon />
                        </SideArrowRight>)}
                    </GridArea>
                )}

                {readAllLink && posts?.length > 3 && (
                    <ReadAllWrap>
                        <ReadAllBtn href={readAllLink}>
                            {readAllText ? readAllText.replace('{cityName}', cityName) : `Read all ${cityName} articles`}
                        </ReadAllBtn>
                    </ReadAllWrap>
                )}
            </div>
        </Section>
    );
}
